/** UI-surface activity types. Merged into ACTIVITY_TYPES.
 *
 * NO CLIENT-TRACKING MEMBERS: a member here must have an emitter, and no client activity-tracking hook exists to emit
 * the page, gallery, modal, offer-form or session tracking events -- each such member would be a
 * `QUAL-ACTIVITY-TYPE-ZERO-READER-001` true positive (zero emitters, zero symbolic references).
 *
 * Both safety checks the operator required for that excluded set are recorded rather than implied. Every excluded value was verified
 * ABSENT from the whole `migrations/**` tree in BOTH the bare (`'x'`) and the SQL-doubled (`''x''`) spelling a seeded
 * `metrics.measurement_formula` wears -- the control being that the same probe DOES find `compliance_scan`,
 * `offer_form_viewed` and `offer_submitted_successfully` there, which is exactly why those three are KEPT below. And
 * every raw-string occurrence anywhere else in the tree resolved to prose (a doc table, a mermaid node, a tombstone
 * comment), never to a producer or a fixture.
 *
 * The excluded set is pinned as `CUT_UI_TRACKING_MEMBERS` in `const/__tests__/event-catalog-stub-ui.test.ts` and
 * `CUT_PAYMENT_FUNNEL_MEMBERS` in the payment sibling, so the keyset cannot regrow them. Adding any of them means
 * shipping its emitter in the same change (member + EventCatalogKey arm + stub entry, atomically).
 */

export const ACTIVITY_TYPES_UI = {
	VIEW: 'view',
	CLICK: 'click',
	PAGE_VIEW: 'page_view',
	NAVIGATION: 'navigation',
	IMAGE_IMPRESSION: 'image_impression',
	// The two offer-funnel members with a LIVE reader: both are queried by seeded kaizen `metrics.measurement_formula`
	// rows in migrations/d1/kaizen/0025_backfill_measurement_formula.sql (`offer-views`, `offer-conversion-rate`,
	// `avg-offer-duration`, and the bid/engagement ratios). Their funnel siblings had no such reader and were cut.
	OFFER_SUBMITTED_SUCCESSFULLY: 'offer_submitted_successfully',
	OFFER_FORM_VIEWED: 'offer_form_viewed',
	BID_PLACED: 'bid_placed',
	PROPERTY_VIEW: 'property_view',
	// Property interaction
	PROPERTY_FILTER: 'property_filter',
	PROPERTY_SORT: 'property_sort',
	PROPERTY_SHARE: 'property_share',
	PROPERTY_COMPARE: 'property_compare',
	PROPERTY_CARD_IMPRESSION: 'property_card_impression',
	ADD_FAVORITE_PROPERTY: 'add_favorite_property',
	REMOVE_FAVORITE_PROPERTY: 'remove_favorite_property',
	// Wishlists (emitted by the flows/user-wishlist-* graphs through ctx.queueActivity under ACTIVITY_TRIGGERS.WEBSOCKET_WISHLIST).
	WISHLIST_CREATED: 'wishlist_created',
	WISHLIST_UPDATED: 'wishlist_updated',
	WISHLIST_DELETED: 'wishlist_deleted',
	WISHLIST_PARTICIPANT_ADDED: 'wishlist_participant_added',
	WISHLIST_PARTICIPANT_REMOVED: 'wishlist_participant_removed',
	WISHLIST_PARTICIPANTS_FETCHED: 'wishlist_participants_fetched',
	WISHLIST_PROPERTY_ADDED: 'wishlist_property_added',
	WISHLIST_PROPERTY_REMOVED: 'wishlist_property_removed',
	WISHLIST_PROPERTIES_FETCHED: 'wishlist_properties_fetched',
	// Bidding & offers
	BID_WON: 'bid_won',
	BID_OUTBID: 'bid_outbid',
	OFFER_CREATED: 'offer_created',
	OFFER_ACCEPTED: 'offer_accepted',
	CONTRACT_SIGNED: 'contract_signed',
	// Financial
	PAYMENT_INITIATED: 'payment_initiated',
	PAYMENT_COMPLETED: 'payment_completed',
	WALLET_BALANCE_VIEW: 'wallet_balance_view',
	// Navigation & search
	MARKET_SEARCH: 'market_search',
	// POINT EVENT, named as one: opening the chat is a single action and this union carries no chat-ended key, so an
	// `_INITIATED` suffix would declare a span no cycle-time query could ever close (QUAL-ACTIVITY-LIFECYCLE-PAIR-001).
	CHAT_OPENED: 'chat_opened',
	MESSAGE_SENT: 'message_sent',
} as const;
